import React from 'react';
import styled from 'styled-components';
import { theme } from '../styles/GlobalStyles';
import { Section } from '../components/Section';
import { Heading } from '../components/Heading';
import { Button } from '../components/Button';
import { motion } from 'framer-motion';

const ProcessHero = styled.section`
  position: relative;
  width: 100vw;
  min-height: 70vh;
  padding-top: 100px; /* Account for standard navbar */
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

const ProcessHeroOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.3) 100%);
  z-index: 1;
`;

const ProcessHeroBg = styled.div`
  position: absolute;
  inset: 0;
  z-index: 0;
  background: url('/img_to_use/pexels-seven11nash-380769.jpg') center/cover no-repeat;
  transform: scale(1);
  animation: zoomProcessHero 18s ease-in-out infinite alternate;
  will-change: transform;
  transition: transform 0.7s cubic-bezier(0.4,0,0.2,1);

  @keyframes zoomProcessHero {
    0% { transform: scale(1);}
    100% { transform: scale(1.08);}
  }

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      animation-play-state: paused;
      transform: scale(1.16);
      transition: transform 0.7s cubic-bezier(0.4,0,0.2,1);
    }
  }
`;

const ProcessHeroContent = styled.div`
  position: relative;
  max-width: 800px;
  margin: 0 auto;
  text-align: center;
  z-index: 2;
  padding: 0 2rem;
`;

const ProcessHeroTitle = styled.h1`
  font-size: 4.5rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
  text-shadow: 2px 2px 8px rgba(0, 0, 0, 0.6), 0 0 12px ${theme.colors.accent}33;
  color: ${theme.colors.accent};
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    font-size: 2.8rem;
  }
`;

const ProcessHeroSubtitle = styled.p`
  font-size: 1.6rem;
  margin-bottom: 2.5rem;
  font-weight: 300;
  text-shadow: 1px 1px 6px rgba(0, 0, 0, 0.8);
  color: #FFFFFF;
  opacity: 0.95;
`;

const ProcessSection = styled.section`
  background: ${theme.colors.background};
  width: 100%;
  padding: clamp(4rem, 8vw, 8rem) 0;
`;

const ProcessStepsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: clamp(1.5rem, 3vw, 2.5rem);
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`;

const StepCard = styled(motion.div)<{ bg: string }>`
  position: relative;
  height: 320px;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: flex-start;
  text-align: left;
  padding: 2rem 2rem 1.5rem 2rem;
  overflow: hidden;
  background: ${({ bg }) => `url(${bg}) center/cover no-repeat`};
  transition: transform 0.3s cubic-bezier(0.4,0,0.2,1), box-shadow 0.3s cubic-bezier(0.4,0,0.2,1);
  will-change: transform, box-shadow;
  cursor: pointer;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(34,49,58,0.10);
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: rgba(34,49,58,0.32);
    z-index: 1;
    border-radius: 16px;
  }
  &:hover {
    transform: scale(1.045) translateY(-4px);
    box-shadow: 0 8px 32px ${theme.colors.accent}2e, 0 2px 12px rgba(34,49,58,0.13);
    z-index: 2;
  }
  h3, p {
    position: relative;
    z-index: 2;
    margin: 0;
  }
  h3 {
    color: ${theme.colors.accent};
    font-size: 1.5rem;
    margin-bottom: 0.7rem;
    font-weight: 900;
    letter-spacing: 1.2px;
    font-family: 'Playfair Display', serif;
  }
  p {
    color: #fff;
    font-size: 1.08rem;
    text-shadow: 0 1px 4px rgba(34,49,58,0.18);
    font-family: 'Inter', sans-serif;
    font-weight: 400;
    line-height: 1.6;
  }
`;

const ProcessBenefitsSection = styled.section`
  background: #f5f6fa;
  width: 100%;
  padding: clamp(4rem, 8vw, 8rem) 0;
  text-align: center;
`;

const BenefitsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: clamp(2rem, 4vw, 3rem);
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`;

const BenefitCard = styled(motion.div)<{ bg: string }>`
  position: relative;
  height: 320px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  padding: 2rem;
  overflow: hidden;
  background: ${({ bg }) => `url(${bg}) center/cover no-repeat`};
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(34,49,58,0.10);
  transition: transform 0.3s cubic-bezier(0.4,0,0.2,1), box-shadow 0.3s cubic-bezier(0.4,0,0.2,1);
  will-change: transform, box-shadow;
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(rgba(34,49,58,0.8), rgba(34,49,58,0.6));
    z-index: 1;
    border-radius: 16px;
  }
  &:hover {
    transform: scale(1.03) translateY(-8px);
    box-shadow: 0 8px 32px ${theme.colors.accent}2e, 0 2px 12px rgba(34,49,58,0.13);
    z-index: 2;
  }
  h3, p {
    position: relative;
    z-index: 2;
    margin: 0;
  }
  h3 {
    color: ${theme.colors.accent};
    font-size: 1.7rem;
    margin-bottom: 1rem;
    font-weight: 900;
    letter-spacing: 1.2px;
    font-family: 'Playfair Display', serif;
  }
  p {
    color: #fff;
    font-size: 1.1rem;
    text-shadow: 0 1px 4px rgba(34,49,58,0.18);
    font-family: 'Inter', sans-serif;
    font-weight: 400;
    line-height: 1.6;
  }
`;

const TestimonialsSection = styled.section`
  background: #fff;
  width: 100%;
  padding: clamp(4rem, 8vw, 8rem) 0;
  text-align: center;
`;

const TestimonialsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: clamp(2rem, 4vw, 3rem);
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const TestimonialCard = styled(motion.div)`
  position: relative;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(34,49,58,0.10);
  padding: 2.5rem 2rem 2rem 2rem;
  text-align: left;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: flex-start;
  min-height: 320px;
  transition: transform 0.3s cubic-bezier(0.4,0,0.2,1), box-shadow 0.3s cubic-bezier(0.4,0,0.2,1);
  will-change: transform, box-shadow;
  &:hover {
    transform: scale(1.03) translateY(-8px);
    box-shadow: 0 8px 32px ${theme.colors.accent}2e, 0 2px 12px rgba(34,49,58,0.13);
    z-index: 2;
  }
  p {
    color: #22313a;
    font-size: 1.1rem;
    font-family: 'Inter', sans-serif;
    font-weight: 400;
    line-height: 1.7;
    margin-bottom: 1.2rem;
  }
  .author {
    font-weight: bold;
    color: ${theme.colors.accent};
    font-size: 1.1rem;
    font-family: 'Playfair Display', serif;
    margin-bottom: 0.2rem;
  }
`;

const NavCTA = styled(Button)`
  margin-left: 2.5rem;
  font-size: 1.1rem;
  background: ${theme.colors.sectionAlt};
  color: #22313a;
  border: 2px solid ${theme.colors.accent};
  padding: 0.8rem 1.7rem;
  border-radius: 8px;
  font-weight: 700;
  box-shadow: 0 4px 16px ${theme.colors.accent}1a;
  transition: all 0.3s cubic-bezier(.4,0,.2,1);

  &:hover {
    background: ${theme.colors.accent};
    color: #22313a;
    box-shadow: 0 6px 24px ${theme.colors.accent}2e;
    transform: translateY(-2px) scale(1.04);
  }
`;

const steps = [
  {
    title: 'Consultation',
    img: '/img_to_use/consultation.jpg',
    desc: 'We begin by understanding your specific staffing requirements, company culture, and project goals.'
  },
  {
    title: 'Talent Search',
    img: '/img_to_use/talent%20search.jpg',
    desc: 'Our team leverages extensive networks and advanced sourcing techniques to identify qualified candidates.'
  },
  {
    title: 'Screening & Evaluation',
    img: '/img_to_use/screening.jpg',
    desc: 'Candidates undergo thorough screening, including skills assessment, background checks, and cultural fit evaluation.'
  },
  {
    title: 'Interview Coordination',
    img: '/img_to_use/cordination.jpg',
    desc: 'We manage the interview process, coordinating schedules and providing feedback for a smooth experience.'
  },
  {
    title: 'Placement & Onboarding',
    img: '/img_to_use/placement.jpg',
    desc: 'Once selected, we assist with placement and provide support during onboarding.'
  },
  {
    title: 'Ongoing Support',
    img: '/img_to_use/support.jpg',
    desc: 'We maintain communication to ensure long-term success for both clients and candidates.'
  }
];

const benefits = [
  {
    title: 'Industry Expertise',
    img: '/img_to_use/expert.jpg',
    desc: "Deep understanding of your sector's needs and challenges."
  },
  {
    title: 'Quality Assurance',
    img: '/img_to_use/quality.jpg',
    desc: 'Rigorous screening and vetting process for all candidates.'
  },
  {
    title: 'Global Reach',
    img: '/img_to_use/global.jpg',
    desc: 'Access to talent pools across multiple regions and markets.'
  }
];

const testimonials = [
  {
    title: 'Tech Startup CEO',
    desc: "De ARC Global's process was seamless and efficient. They understood our needs perfectly and delivered exceptional candidates."
  },
  {
    title: 'Healthcare Director',
    desc: "The team's attention to detail and commitment to quality made all the difference in our recruitment process."
  }
];

const ProcessPage: React.FC = () => {
  return (
    <>
      <ProcessHero>
        <ProcessHeroBg />
        <ProcessHeroOverlay />
        <ProcessHeroContent>
          <ProcessHeroTitle>Our Recruitment Process</ProcessHeroTitle>
          <ProcessHeroSubtitle>
            A proven, step-by-step approach to finding the perfect talent for your business
          </ProcessHeroSubtitle>
        </ProcessHeroContent>
      </ProcessHero>

      <ProcessSection>
        <Heading level="h2" align="center" style={{ fontFamily: theme.fonts.secondary, color: theme.colors.text, fontWeight: theme.typography.h2.fontWeight, marginBottom: '2.5rem' }}>
          How We Work
        </Heading>
        <ProcessStepsGrid>
          {steps.map((step, idx) => (
            <StepCard
              key={step.title}
              bg={step.img}
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.7, ease: 'easeOut', delay: idx * 0.1 }}
            >
              <h3>{step.title}</h3>
              <p>{step.desc}</p>
            </StepCard>
          ))}
        </ProcessStepsGrid>
      </ProcessSection>

      <ProcessBenefitsSection>
        <Heading level="h2" align="center" style={{ marginBottom: '3rem' }}>
          Why Our Process Works
        </Heading>
        <BenefitsGrid>
          {benefits.map((benefit, idx) => (
            <BenefitCard
              key={benefit.title}
              bg={benefit.img}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.7, delay: idx * 0.1 }}
            >
              <h3>{benefit.title}</h3>
              <p>{benefit.desc}</p>
            </BenefitCard>
          ))}
        </BenefitsGrid>
      </ProcessBenefitsSection>

      <TestimonialsSection>
        <Heading level="h2" align="center" style={{ marginBottom: '3rem' }}>
          Client Testimonials
        </Heading>
        <TestimonialsGrid>
          {testimonials.map((testimonial, idx) => (
            <TestimonialCard
              key={testimonial.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.7, delay: idx * 0.1 }}
            >
              <p>{testimonial.desc}</p>
              <div className="author">{testimonial.title}</div>
            </TestimonialCard>
          ))}
        </TestimonialsGrid>
      </TestimonialsSection>

      <Section>
        <Heading level="h2" align="center">
          Experience the difference
        </Heading>
        <p style={{ textAlign: 'center', maxWidth: '800px', margin: '0 auto' }}>
          Let's discuss how our proven process can help you find the perfect talent for
          your organization.
        </p>
        <div style={{ textAlign: 'center', marginTop: theme.spacing.xl }}>
          <NavCTA variant="primary" size="large">
            Start the Conversation
          </NavCTA>
        </div>
      </Section>
    </>
  );
};

export default ProcessPage; 